/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable object-curly-newline */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import { UserContext } from '../../../contexts/User';
import { formatDate } from '../../../utils/time';
import DataTable from '../../../components/DataTable';
import StatusPill from '../../../components/StatusPill';
import MemberCell from './MemberCell';
import AlertFilterMenu from './AlertFilterMenu';
import ViewIssueModal from './ViewIssueModal';

const PAGE_SIZE = 10;
const dash = <span className="text-ink-500">—</span>;

const AlertTypes = [
  { id: 0, name: 'All' }, { id: 1, name: 'HR' }, { id: 2, name: 'IT' }, { id: 3, name: 'Infosec' }, { id: 4, name: 'Other' },
];
const Status = [
  { id: 0, name: 'All' }, { id: 1, name: 'Acknowledged' }, { id: 2, name: 'Pending' }, { id: 3, name: 'Resolved' }, { id: 4, name: 'Error' },
];

const SUBTITLE = {
  HR: 'Employee Engagement Monitoring',
  IT: 'Device & software compliance monitoring',
  Infosec: 'Security & policy violation monitoring',
};

const engTone = (pct) => (pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error'); // eslint-disable-line no-nested-ternary

function BotAlerts({ category }) {
  const { search } = useLocation();
  const { activeWorkspace } = useContext(UserContext);
  const query = new URLSearchParams(search);
  const page = query.get('page');
  const history = useHistory();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [alerts, setAlerts] = useState([]);
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [members, setMembers] = useState([{ id: 0, name: 'All' }]);
  const [memberId, setMemberId] = useState(0);
  const [alertTypeId, setAlertTypeId] = useState(0);
  const [statusId, setStatusId] = useState(0);

  const onClickView = (alert) => { setSelectedAlert(alert); setIsOpenModal(true); };
  const handleClose = () => setIsOpenModal(false);
  const handlePageChange = (next) => { setCurrentPage(next); history.push(`?page=${next}`); };

  const getAlerts = async (params) => {
    setIsLoading(true);
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getAlerts(activeWorkspace.id),
      params: { pageSize: PAGE_SIZE, alertType: [category], sortKey, sortOrder, ...params },
    });
    if (response.success) {
      setAlerts(response.data?.alerts ?? []);
      setTotalPages(response.data?.totalPages ?? 1);
    }
    setIsLoading(false);
  };

  const getAllMembers = async () => {
    const response = await sendRequest({ method: 'get', url: API.WORKSPACE.members(activeWorkspace.id) });
    if (response.success) {
      const mapped = response.data?.members.map((m) => ({ id: m.id, name: m.user?.fullName }));
      setMembers([{ id: 0, name: 'All' }, ...mapped]);
    }
  };

  const handleReset = () => {
    setMemberId(0);
    setAlertTypeId(0);
    setStatusId(0);
  };

  const handleSearch = () => {
    const at = AlertTypes.find((t) => t.id === Number(alertTypeId));
    const st = Status.find((s) => s.id === Number(statusId));
    getAlerts({
      memberIds: Number(memberId) === 0 ? null : [Number(memberId)],
      status: !st || st.id === 0 ? null : [st.name],
      alertType: !at || at.id === 0 ? null : [at.name],
      page: currentPage,
    });
  };

  const toggleSort = (key) => {
    const nextOrder = key === sortKey && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortOrder(nextOrder);
    setCurrentPage(1);
  };

  useEffect(() => { getAlerts({ page: currentPage }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentPage, sortKey, sortOrder]);
  useEffect(() => { getAllMembers(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const columns = [
    { key: 'user', label: 'Employee Name', width: 240, flex: true, render: (a) => <MemberCell id={a?.member?.id} firstName={a?.member?.user?.firstName} lastName={a?.member?.user?.lastName} email={a?.member?.user?.email} profilePicture={a?.member?.user?.profilePicture} /> },
    {
      key: 'engagementScore',
      label: 'Engagement Score',
      width: 150,
      render: (a) => {
        const total = a?.member?.dailyActivity?.[0]?.totalSeconds || 0;
        const active = a?.member?.dailyActivity?.[0]?.activeSeconds || 0;
        const pct = total === 0 ? 0 : Math.round((active * 100) / total);
        return <StatusPill tone={engTone(pct)}>{`${pct}%`}</StatusPill>;
      },
    },
    { key: 'hoursWorked', label: 'Hours Worked', width: 130, render: (a) => <span className="tabular-nums text-ink-900">{`${Math.round((a?.member?.dailyActivity?.[0]?.totalSeconds || 0) / 3600)}h`}</span> },
    { key: 'idleTime', label: 'Idle Time', width: 120, render: (a) => { const t = a?.member?.dailyActivity?.[0]?.totalSeconds || 0; const ac = a?.member?.dailyActivity?.[0]?.activeSeconds || 0; return <span className="tabular-nums text-ink-700">{`${Math.round((t - ac) / 3600)}h`}</span>; } },
    { key: 'alertsTriggered', label: 'Alerts Triggered', width: 220, flex: true, render: (a) => <span className="text-ink-700 truncate block" title={a?.alertType?.triggerCondition}>{a?.alertType?.triggerCondition || dash}</span> },
    { key: 'createdAt', label: 'Date', sortable: true, width: 130, render: (a) => <span className="text-ink-700 whitespace-nowrap">{formatDate(a.createdAt)}</span> },
    {
      key: 'actions',
      label: 'Action',
      width: 130,
      align: 'center',
      render: (a) => (
        <button type="button" onClick={() => onClickView(a)} className="inline-flex items-center justify-center px-3 py-1.5 rounded-[8px] text-[11px] font-semibold uppercase tracking-wider text-ink-900 bg-white border border-rule hover:border-brand-500 hover:text-brand-700 transition whitespace-nowrap" style={{ borderStyle: 'solid', letterSpacing: '0.05em' }}>
          View Issue
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-ink-900 mb-0.5">{`${category} Bot`}</h4>
          <p className="text-sm text-ink-500">{SUBTITLE[category] || 'Engagement monitoring'}</p>
        </div>
        <AlertFilterMenu
          onReset={handleReset}
          onSearch={handleSearch}
          groups={[
            { label: 'Employee Name', options: members, value: memberId, onChange: setMemberId },
            { label: 'Alert Type', options: AlertTypes, value: alertTypeId, onChange: setAlertTypeId },
            { label: 'Status', options: Status, value: statusId, onChange: setStatusId },
          ]}
        />
      </div>
      <DataTable
        columns={columns}
        rows={alerts}
        getRowKey={(a) => a.id}
        sortKey={sortKey}
        sortDir={sortOrder}
        onSort={toggleSort}
        loading={isLoading}
        emptyText="There are no data yet."
        page={currentPage}
        pageSize={PAGE_SIZE}
        total={totalPages * PAGE_SIZE}
        onPageChange={handlePageChange}
        minWidth={1120}
      />
      <ViewIssueModal selectedAlert={selectedAlert} isOpen={isOpenModal} handleClose={handleClose} />
    </>
  );
}

BotAlerts.propTypes = { category: PropTypes.string.isRequired };

export default BotAlerts;
